import { forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import { filter, get, noop } from 'lodash';
import { Form } from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import { MultiSelect } from 'react-multi-select-component';
import { useNodeContext } from 'contexts/NodeProvider';
import ErrorMessage from 'components/Common/ErrorMessage';

const MultiSelectWithController = forwardRef(
    (
        {
            control,
            name,
            defaultValue,
            label,
            options = [],
            disabled,
            onChange = noop,
            required,
            ariaLabel,
            hasSelectAll,
            tooltip,
            registerRef = true,
            ...rest
        },
        ref
    ) => {
        const _ref = useRef();
        const { nodesRef } = useNodeContext();

        useImperativeHandle(ref, () => _ref.current);

        return (
            <Controller
                control={control}
                name={name}
                defaultValue={defaultValue}
                render={({
                    field: { onChange: handleChange, value, ref: rhfRef },
                    fieldState: { error },
                }) => {
                    const onRefChange = useCallback((node) => {
                        if (registerRef) {
                            _ref.current = node;
                            rhfRef = _ref;
                            if (nodesRef) {
                                nodesRef.current[name] = node;
                            }
                        }
                    }, []);
                    return (
                        <div className="multi-select-field-container">
                            <Form.Group
                                className="multi-select-group"
                                data-testid={name}
                            >
                                <Form.Label
                                    className="multi-select-label"
                                    id={`label-question-select-${name}`}
                                >
                                    {label}{' '}
                                    <span className="text-danger">
                                        {required ? ' *' : ' '}
                                    </span>
                                </Form.Label>
                                <MultiSelect
                                    ref={onRefChange}
                                    id={name}
                                    aria-labelledby={`label-question-select-${name}`}
                                    name={name}
                                    disabled={disabled}
                                    onChange={(items) => {
                                        const valueStr = items
                                            .map((item) => item.value)
                                            .join(',');
                                        onChange(valueStr);
                                        handleChange(valueStr);
                                    }}
                                    value={
                                        filter(options, (item) =>
                                            value
                                                ?.split(',')
                                                .some(
                                                    (val) =>
                                                        val ===
                                                        item.value.toString()
                                                )
                                        ) ?? []
                                    }
                                    options={options}
                                    labelledBy={label}
                                    hasSelectAll={hasSelectAll}
                                    {...rest}
                                />
                            </Form.Group>
                            <ErrorMessage message={get(error, 'message')} />
                        </div>
                    );
                }}
            />
        );
    }
);

export default MultiSelectWithController;
